import React, { createContext, useContext, useState, useCallback, useMemo, useRef, ReactNode } from 'react';
import type { GenerationProgressState } from '../hooks/useGeneration';

interface GenerationContextType {
  isGenerating: boolean;
  isGeneratingBatch: boolean; // True uniquement pendant une génération groupée (multi-posts)
  setIsGenerating: (value: boolean) => void;
  setIsGeneratingBatch: (value: boolean) => void;
  generationCount: number; // Number of active generations
  startGeneration: () => void;
  endGeneration: () => void;
  // Global progress state — synced from useGeneration hook instances
  progressState: GenerationProgressState | null;
  setGlobalProgressState: (state: GenerationProgressState | null) => void;
  // Global cancel function — registered by the active useGeneration instance
  cancelGeneration: (() => void) | null;
  registerCancelGeneration: (fn: (() => void) | null) => void;
  // Dismissible overlay visibility — separate from isGenerating
  showGenerationOverlay: boolean;
  setShowGenerationOverlay: (value: boolean) => void;
}

const GenerationContext = createContext<GenerationContextType | undefined>(undefined);

export function GenerationProvider({ children }: { children: ReactNode }) {
  const [generationCount, setGenerationCount] = useState(0);
  const [batchGenerationCount, setBatchGenerationCount] = useState(0);
  const [progressState, setGlobalProgressState] = useState<GenerationProgressState | null>(null);
  const cancelRef = useRef<(() => void) | null>(null);
  const [cancelTick, forceUpdate] = useState(0);
  const [showGenerationOverlay, setShowGenerationOverlay] = useState(false);

  const registerCancelGeneration = useCallback((fn: (() => void) | null) => {
    cancelRef.current = fn;
    forceUpdate(n => n + 1);
  }, []);

  const startGeneration = useCallback(() => {
    setGenerationCount(prev => prev + 1);
    setShowGenerationOverlay(true); // auto-show overlay when generation starts
  }, []);

  const endGeneration = useCallback(() => {
    setGenerationCount(prev => Math.max(0, prev - 1));
    // Don't auto-hide overlay so user can see final result; wand button disappears when done
  }, []);

  const setIsGenerating = useCallback((value: boolean) => {
    if (value) {
      startGeneration();
    } else {
      endGeneration();
    }
  }, [startGeneration, endGeneration]);

  const setIsGeneratingBatch = useCallback((value: boolean) => {
    // La génération batch est gérée indépendamment de isGenerating global.
    // Le bouton "+" (création individuelle) doit rester accessible pendant une génération batch.
    if (value) {
      setBatchGenerationCount(prev => prev + 1);
    } else {
      setBatchGenerationCount(prev => Math.max(0, prev - 1));
    }
  }, []);

  const value = useMemo(
    () => ({
      isGenerating: generationCount > 0,
      isGeneratingBatch: batchGenerationCount > 0,
      setIsGenerating,
      setIsGeneratingBatch,
      generationCount,
      startGeneration,
      endGeneration,
      progressState,
      setGlobalProgressState,
      cancelGeneration: cancelRef.current,
      registerCancelGeneration,
      showGenerationOverlay,
      setShowGenerationOverlay,
    }),
    [
      generationCount,
      batchGenerationCount,
      setIsGenerating,
      setIsGeneratingBatch,
      startGeneration,
      endGeneration,
      progressState,
      registerCancelGeneration,
      showGenerationOverlay,
      cancelTick,
    ]
  );

  return (
    <GenerationContext.Provider value={value}>
      {children}
    </GenerationContext.Provider>
  );
}

export function useGenerationContext() {
  const context = useContext(GenerationContext);
  if (context === undefined) {
    throw new Error('useGenerationContext must be used within a GenerationProvider');
  }
  return context;
}
